import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { 
  FileSpreadsheet, 
  Check,
  X,
  ArrowRight,
  Calculator,
  Zap,
  Clock,
  DollarSign,
  Users,
  Shield,
  Sparkles,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const CompareHubSpotPage = () => {
  const [contactCount, setContactCount] = useState(10000);
  const [cleaningFrequency, setCleaningFrequency] = useState('monthly'); // monthly, quarterly, yearly
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // HubSpot pricing (annual)
  const hubspotPricing = {
    starter: { monthly: 9, name: 'Starter', features: ['Data sync', 'Custom field mappings', 'No duplicate management', 'No bulk cleaning'] },
    professional: { monthly: 720, name: 'Professional', features: ['Bulk duplicate management', 'Data quality overview', 'AI formatting recommendations'] },
    enterprise: { monthly: 2000, name: 'Enterprise', features: ['All Pro features', 'Advanced data calculations', 'Data warehouse integrations'] }
  };

  // CRM Data Cleaner is free and unlimited
  const crmCleanerCost = 0;

  // Calculate costs
  const cleaningsPerYear = cleaningFrequency === 'monthly' ? 12 : cleaningFrequency === 'quarterly' ? 4 : 1;
  const rowsPerYear = contactCount * cleaningsPerYear;

  const crmCleanerYearlyCost = crmCleanerCost;
  const hubspotProYearlyCost = hubspotPricing.professional.monthly * 12;
  const savings = hubspotProYearlyCost - crmCleanerYearlyCost;
  const savingsPercent = Math.round((savings / hubspotProYearlyCost) * 100);

  // Feature comparison data
  const featureComparison = [
    { 
      category: 'Nettoyage des données',
      features: [
        { name: 'Correction automatique des emails (typos)', crm: true, hubspot: false, hubspotNote: 'Manuel uniquement' },
        { name: 'Standardisation des numéros de téléphone', crm: true, hubspot: true, hubspotNote: 'Via Operations Hub Pro' },
        { name: 'Normalisation des noms (Title Case)', crm: true, hubspot: true, hubspotNote: 'Via workflows' },
        { name: 'Extraction prénom/nom depuis email', crm: true, hubspot: false },
        { name: 'Détection du genre depuis prénom', crm: true, hubspot: false },
        { name: 'Normalisation des revenus', crm: true, hubspot: false },
        { name: 'Standardisation des valeurs dropdown', crm: true, hubspot: true, hubspotNote: 'Configuration manuelle' },
        { name: 'Nettoyage en masse (bulk)', crm: true, hubspot: true, hubspotNote: 'Pro+ uniquement ($720/mois)' },
      ]
    },
    {
      category: 'Gestion des doublons',
      features: [
        { name: 'Détection des doublons', crm: true, hubspot: true, hubspotNote: 'Pro+ uniquement' },
        { name: 'Fusion automatique', crm: true, hubspot: true, hubspotNote: 'Pro+ uniquement' },
        { name: 'Prévisualisation avant fusion', crm: true, hubspot: true },
      ]
    },
    {
      category: 'Validation des données',
      features: [
        { name: 'Vérification MX des emails', crm: true, hubspot: false },
        { name: 'Détection emails invalides', crm: true, hubspot: false },
        { name: 'Validation format téléphone', crm: true, hubspot: false },
      ]
    },
    {
      category: 'Facilité d\'utilisation',
      features: [
        { name: 'Import CSV/Excel direct', crm: true, hubspot: false, hubspotNote: 'Nécessite import HubSpot' },
        { name: 'Prévisualisation des changements', crm: true, hubspot: false },
        { name: 'Export fichier nettoyé', crm: true, hubspot: false, hubspotNote: 'Données restent dans HubSpot' },
        { name: 'Utilisation sans compte CRM', crm: true, hubspot: false },
        { name: 'Interface en français', crm: true, hubspot: true },
      ]
    },
    {
      category: 'Accès',
      features: [
        { name: 'Entièrement gratuit', crm: true, hubspot: false },
        { name: 'Pas d\'engagement annuel requis', crm: true, hubspot: false },
        { name: 'Aucune carte de crédit requise', crm: true, hubspot: false, hubspotNote: 'Nécessite contact commercial' },
      ]
    }
  ];

  const displayedFeatures = showAllFeatures ? featureComparison : featureComparison.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
            <TrendingDown className="w-4 h-4" />
            Économisez jusqu'à {savingsPercent}% vs HubSpot Data Hub
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            CRM Data Cleaner vs<br />
            <span className="text-orange-500">HubSpot Operations Hub</span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Comparez les fonctionnalités de nettoyage de données et découvrez 
            pourquoi des milliers d'équipes choisissent CRM Data Cleaner pour 
            préparer leurs données avant import CRM.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Essayer gratuitement
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Comparison Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* CRM Data Cleaner Card */}
            <div className="relative bg-gradient-to-br from-indigo-50 to-white rounded-2xl border-2 border-indigo-200 p-8 shadow-lg">
              <div className="absolute -top-4 left-6 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Recommandé
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <FileSpreadsheet className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">CRM Data Cleaner</h3>
                  <p className="text-slate-500">Nettoyage avant import</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-indigo-600">
                    Gratuit
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Nettoyage illimité, sans carte de crédit
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  'Nettoyage instantané en 3 secondes',
                  'Correction automatique des emails',
                  'Extraction nom depuis email (IA)',
                  'Prévisualisation avant application',
                  'Export CSV/Excel prêt pour import',
                  'Pas besoin de compte HubSpot'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/register">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Commencer gratuitement
                </Button>
              </Link>
            </div>

            {/* HubSpot Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">H</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">HubSpot Operations Hub</h3>
                  <p className="text-slate-500">Nettoyage dans le CRM</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-slate-900">$720</span>
                  <span className="text-slate-500">/mois</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Plan Professional • Minimum pour le nettoyage en masse
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  { text: 'Gestion des doublons', included: true },
                  { text: 'Recommandations IA formatage', included: true },
                  { text: 'Aperçu qualité des données', included: true },
                  { text: 'Correction automatique emails', included: false },
                  { text: 'Extraction nom depuis email', included: false },
                  { text: 'Nettoyage avant import', included: false },
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    )}
                    <span className={!feature.included ? 'text-slate-400' : ''}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button variant="outline" className="w-full" disabled>
                Nécessite abonnement HubSpot
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Calculator className="w-4 h-4" />
              Calculateur d'économies
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Combien pouvez-vous économiser ?
            </h2>
            <p className="text-slate-600">
              Ajustez les paramètres pour voir vos économies potentielles
            </p>
          </div>

          <div className="bg-white rounded-2xl border shadow-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre de contacts à nettoyer
                </label>
                <input
                  type="range"
                  min="1000"
                  max="100000"
                  step="1000"
                  value={contactCount}
                  onChange={(e) => setContactCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-2xl font-bold text-indigo-600">
                    {contactCount.toLocaleString()}
                  </span>
                  <span className="text-slate-500">contacts</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fréquence de nettoyage
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'monthly', label: 'Mensuel' },
                    { value: 'quarterly', label: 'Trimestriel' },
                    { value: 'yearly', label: 'Annuel' }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setCleaningFrequency(freq.value)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        cleaningFrequency === freq.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="grid md:grid-cols-3 gap-6 pt-8 border-t">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">HubSpot Operations Hub Pro</p>
                <p className="text-3xl font-bold text-slate-900">
                  ${hubspotProYearlyCost.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">/an</p>
              </div>

              <div className="text-center p-4 bg-indigo-50 rounded-xl border-2 border-indigo-200">
                <p className="text-sm text-indigo-600 mb-1">CRM Data Cleaner</p>
                <p className="text-3xl font-bold text-indigo-600">
                  Gratuit
                </p>
              </div>

              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-emerald-600 mb-1">Vos économies</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${savings.toLocaleString()}
                </p>
                <p className="text-sm text-emerald-500">/an ({savingsPercent}%)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Feature Comparison Table */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Comparaison détaillée des fonctionnalités
            </h2>
            <p className="text-slate-600">
              Découvrez ce qui différencie CRM Data Cleaner de HubSpot Operations Hub
            </p>
          </div>

          <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-slate-50 border-b">
              <div className="p-4 font-semibold text-slate-700">Fonctionnalité</div>
              <div className="p-4 font-semibold text-center bg-indigo-50 text-indigo-700">
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  CRM Data Cleaner
                </div>
              </div>
              <div className="p-4 font-semibold text-center text-slate-700">
                <div className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">H</span>
                  HubSpot
                </div>
              </div>
            </div>

            {/* Table Body */}
            {displayedFeatures.map((category, catIdx) => (
              <div key={catIdx}>
                <div className="bg-slate-100 px-4 py-2 font-semibold text-slate-800 border-b">
                  {category.category}
                </div>
                {category.features.map((feature, featIdx) => (
                  <div 
                    key={featIdx} 
                    className={`grid grid-cols-3 border-b last:border-b-0 ${
                      featIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    <div className="p-4 text-slate-700">{feature.name}</div>
                    <div className="p-4 flex justify-center bg-indigo-50/30">
                      {feature.crm ? (
                        <Check className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="p-4 flex flex-col items-center">
                      {feature.hubspot ? (
                        <>
                          <Check className="w-5 h-5 text-emerald-500" />
                          {feature.hubspotNote && (
                            <span className="text-xs text-slate-400 mt-1 text-center">
                              {feature.hubspotNote}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <X className="w-5 h-5 text-red-400" />
                          {feature.hubspotNote && (
                            <span className="text-xs text-slate-400 mt-1 text-center">
                              {feature.hubspotNote}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Show More Button */}
          <div className="text-center mt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="gap-2"
            >
              {showAllFeatures ? (
                <>
                  Voir moins
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Voir toutes les fonctionnalités
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-indigo-600">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Pourquoi choisir CRM Data Cleaner ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: '98% moins cher',
                description: 'À partir de $19/mois vs $720/mois pour HubSpot Operations Hub Pro'
              },
              {
                icon: Zap,
                title: 'Nettoyage instantané',
                description: '3 secondes pour nettoyer 1000 lignes. Pas besoin d\'attendre les workflows HubSpot.'
              },
              {
                icon: Shield,
                title: 'Avant l\'import',
                description: 'Nettoyez vos données AVANT de polluer votre CRM avec des données sales.'
              }
            ].map((benefit, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-indigo-100">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Box */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <div className="flex gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-2">
                  Le problème avec le nettoyage DANS le CRM
                </h3>
                <p className="text-amber-800 mb-4">
                  HubSpot Operations Hub nettoie vos données <strong>après</strong> leur import dans le CRM. 
                  Cela signifie que vos données sales sont déjà dans votre système, créant :
                </p>
                <ul className="space-y-2 text-amber-800">
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Des doublons qui déclenchent des workflows indésirables
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Des emails envoyés à des adresses invalides (impact délivrabilité)
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Des données historiques polluées difficiles à corriger
                  </li>
                </ul>
                <p className="text-amber-800 mt-4 font-medium">
                  Avec CRM Data Cleaner, nettoyez <strong>AVANT</strong> l'import et gardez votre CRM propre dès le départ.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Prêt à économiser ${savings.toLocaleString()}/an ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Gratuit et illimité. Pas de carte bancaire requise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Essayer gratuitement
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-slate-400 text-sm">
          <p>
            Les prix HubSpot sont basés sur les tarifs publics disponibles sur hubspot.com en janvier 2026.
            Les fonctionnalités comparées sont basées sur la documentation officielle HubSpot.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CompareHubSpotPage;
